import { ShareLinkFile } from "../entities";
import { ItemActivation } from "./fileCard";
import { FileListHeader, FileListRow } from "./fileListRow";
import { FileSort, FileSortKey } from "../utils/fileSort";

export type FileListProps = {
  files: ShareLinkFile[];
  selectedUids: Set<string>;
  onToggleSelect: (uid: string) => void;
  /** Invoked with the file when a row is activated to open its preview. */
  onOpenPreview: (file: ShareLinkFile) => void;
  /** True while a marquee drag is active (suppresses row hover + pointer cursor). */
  selecting?: boolean;
  /**
   * Active column sort, or `null` while nothing is sorted. {@link
   * FileListProps.onSort} alone makes the column headers clickable sort
   * controls; the caller owns the ordering of `files` (this component never
   * reorders them).
   */
  sort?: FileSort | null;
  onSort?: (key: FileSortKey) => void;
  /** Click activation model (see {@link ItemActivation}). Defaults to `"preview"`. */
  activation?: ItemActivation;
  /** Plain-click exclusive select for `"select"` activation. */
  onSelectExclusive?: (uid: string) => void;
  /** Shift+click range select for `"select"` activation. */
  onSelectRange?: (uid: string) => void;
  /**
   * Keep every row's checkbox visible instead of revealing it on hover /
   * selection. Set on touch devices, which have no hover to surface it.
   */
  checkboxAlwaysVisible?: boolean;
};

/**
 * Column-header + rows list of files — the compact (narrow-viewport) file
 * layout. Presentational: the selection state, the display order and the
 * single, shared preview overlay are owned by the parent. `selectionActive` is
 * derived from the selection so that once anything is selected, a tap toggles
 * selection rather than opening the preview (see {@link FileListRow}).
 */
export function FileList({
  files,
  selectedUids,
  onToggleSelect,
  onOpenPreview,
  selecting = false,
  sort,
  onSort,
  activation = "preview",
  onSelectExclusive,
  onSelectRange,
  checkboxAlwaysVisible = false,
}: FileListProps) {
  const selectionActive = selectedUids.size > 0;
  return (
    <div className="pb-6">
      <FileListHeader sort={sort} onSort={onSort} />
      <div className="flex flex-col gap-1">
        {files.map((file) => (
          <FileListRow
            key={file.uid}
            file={file}
            isSelected={selectedUids.has(file.uid)}
            onToggleSelect={onToggleSelect}
            onSelectExclusive={onSelectExclusive}
            onSelectRange={onSelectRange}
            onOpenPreview={onOpenPreview}
            selectionActive={selectionActive}
            activation={activation}
            selecting={selecting}
            checkboxAlwaysVisible={checkboxAlwaysVisible}
          />
        ))}
      </div>
    </div>
  );
}
